import { DateService, PulseEnvironment } from '@pulse/browser-shared';
import { sleep } from '@pulse/common';
import { Subject } from 'rxjs';
import { valueSize } from '../../sizeof';
import { binaryMimeTypes, textMimeTypes } from '../measurable-types';
import { RequestStats } from '../request-stats';
import { parseContentTypeHeader, requestURL } from '../utils';
interface PulseXHRState {
    url: string;
    method: string;
    start: number;
    end: number;
    aborted: boolean;
    requestHeaders: Headers;
    requestBody?: Document | Blob | BufferSource | FormData | URLSearchParams | string | null;
    hasError: boolean;
}

interface PulseXHR extends XMLHttpRequest {
    ___pulse: PulseXHRState;
}

type XHRHandler<M extends keyof XMLHttpRequest> = (
    request: XMLHttpRequest,
    state: PulseXHRState,
    original: (...params: Parameters<XMLHttpRequest[M]>) => ReturnType<XMLHttpRequest[M]>,
    ...params: Parameters<XMLHttpRequest[M]>
) => ReturnType<XMLHttpRequest[M]>;

function safeToMeasureBodySize(body?: any, contentType?: string): boolean {
    if (!contentType || !body) {
        return false;
    }

    if (textMimeTypes.has(contentType)) {
        return true;
    }

    if (binaryMimeTypes.has(contentType)) {
        try {
            return body instanceof Blob || body instanceof ArrayBuffer;
        } catch (ex) {
            console.error(ex);
        }
    }

    return false;
}

export function patchXHR(date: DateService, env: PulseEnvironment) {
    const requests = new Subject<RequestStats>();

    function report(
        req: PulseXHR,
        state: PulseXHRState,
        e: ProgressEvent<XMLHttpRequestEventTarget>,
    ) {
        const requestContentType = parseContentTypeHeader(state.requestHeaders.get('content-type'));

        let responseBodySize: number | undefined = undefined;
        let responseContentType: string | undefined = undefined;
        let report: RequestStats = {
            url: state.url,
            method: state.method,
            start: state.start,
            fetchEnd: state.end,
            end: state.end,
            aborted: Boolean(state.aborted),
            requestContentType,
            responseStatus: req.status,
            initiator: 'xhr',
        };

        if (state.hasError) {
            requests.next(report);

            return;
        }

        try {
            const header = req.getResponseHeader('content-type');
            responseContentType = parseContentTypeHeader(header);
        } catch (err) {
            console.error(err);
        }

        if (e.lengthComputable && typeof e.total === 'number') {
            responseBodySize = e.total;
        } else {
            try {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const responseSize = Number(req.getResponseHeader('content-length')!);

                if (Number.isSafeInteger(responseSize)) {
                    report.responseBodySize = responseSize;
                }
            } catch (err) {
                console.error(err);
            }
        }

        if (
            typeof report.responseBodySize !== 'number' &&
            safeToMeasureBodySize(req.response, responseContentType)
        ) {
            responseBodySize = valueSize(req.response, env.win);
        }

        report = {
            ...report,
            responseBodySize,
            responseContentType,
            end: date.hrt(),
        };

        requests.next(report);
    }

    function init(req: PulseXHR) {
        if (req.___pulse) {
            return req.___pulse;
        }

        const state: PulseXHRState = {
            requestHeaders: new Headers(),
            hasError: false,
        } as any;

        req.___pulse = state;

        req.addEventListener('error', () => {
            state.hasError = true;
        });

        req.addEventListener('timeout', () => {
            state.hasError = true;
            state.aborted = true;
        });

        req.addEventListener('loadend', (e) => {
            state.end = date.hrt();
            sleep(0)
                .then(() => report(req, state, e))
                .catch((err) => console.error(err));
        });

        return state;
    }

    function overrideXHRMethod<M extends keyof XMLHttpRequest>(
        methodName: M,
        handler: XHRHandler<M>,
    ) {
        const original = XMLHttpRequest.prototype[methodName];

        if (typeof original !== 'function') {
            return;
        }

        function container(this: XMLHttpRequest, ...params: Parameters<XMLHttpRequest[M]>) {
            const req = this as PulseXHR;
            const state = init(req);

            return handler(req, state, original.bind(req), ...params);
        }

        XMLHttpRequest.prototype[methodName] = container as any;
    }

    overrideXHRMethod('setRequestHeader', (req, state, setRequestHeader, ...params) => {
        const [name, value] = params;

        try {
            state.requestHeaders.set(name, value);
        } catch (err) {
            console.error(err);
        }

        setRequestHeader.apply(req, params);
    });

    overrideXHRMethod('open', (req, state, open, ...params) => {
        const [method, url] = params;

        open.apply(req, params);

        state.method = method;
        state.url = requestURL(url, env) || '';
    });

    overrideXHRMethod('send', (req, state, send, ...params) => {
        state.start = date.hrt();

        send.apply(req, params);
    });

    overrideXHRMethod('abort', (req, state, abort) => {
        state.aborted = true;
        abort.apply(req);
    });

    return requests.asObservable();
}
